// SMSComponent.jsx
// SMS Modal for chatting with clients via GHL.
// Outgoing messages go through send-lead-sms Edge Function (GHL).
// Conversation history pulled from `messages` table.

import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#fafaf7", cream:"#f8f5f0", surface:"#fff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.10)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.08)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f5f1ec",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

export function SMSModal({ client, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const scrollRef = useRef(null);

  const clientName = client?.name || "Client";
  const phone = client?.phone || "";

  useEffect(() => { loadMessages(); }, [client?.id]);
  useEffect(() => {
    // Auto-scroll to bottom
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const loadMessages = async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.from("messages")
        .select("*")
        .eq("client_id", client.id)
        .order("created_at", { ascending: true })
        .limit(200);
      if (error) console.warn("messages load failed:", error.message);
      setMessages(data || []);
    } catch (e) {
      console.warn("Messages query failed:", e);
      setMessages([]);
    } finally { setLoading(false); }
  };

  const send = async () => {
    if (!text.trim()) return;
    if (!phone) { setError("This client has no phone number"); return; }

    setSending(true); setError(""); setSuccess("");
    const messageText = text.trim();

    // Optimistic UI - show message immediately
    const tempId = "temp-" + Date.now();
    const tempMsg = {
      id: tempId,
      client_id: client.id,
      direction: "outbound",
      body: messageText,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages(m => [...m, tempMsg]);
    setText("");

    try {
      const response = await supabase.functions.invoke("send-lead-sms", {
        body: { client_id: client.id, message: messageText },
      });

      const { error } = response;
      let data = response.data;
      // supabase-js sometimes returns body as raw string - parse it ourselves
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch (e) { console.warn("[SMS] could not parse data string:", e); }
      }

      if (data?.message_id || data?.ok === true) {
        setSuccess("✓ Sent");
        setTimeout(() => setSuccess(""), 2000);
        await loadMessages();
        return;
      }

      if (error) throw new Error(error.message || error.context?.error || "Send failed");
      if (data?.error) throw new Error(data.error);
      throw new Error("Server returned unexpected response");
    } catch (e) {
      console.error("[SMS] send failed:", e);
      setMessages(m => m.filter(x => x.id !== tempId));
      setError(e.message || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:2400,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)",padding:14}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg,width:"100%",maxWidth:540,height:"min(720px, 90vh)",borderRadius:16,overflow:"hidden",boxShadow:"0 12px 48px rgba(0,0,0,0.25)",display:"flex",flexDirection:"column",fontFamily:F}}>

        {/* Header */}
        <div style={{padding:"14px 18px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:9,letterSpacing:"0.15em",opacity:0.85,fontWeight:700}}>💬 SMS · via GoHighLevel</div>
            <div style={{fontSize:18,fontFamily:S,fontWeight:700,marginTop:2}}>{clientName}</div>
            {phone && <div style={{fontSize:11,opacity:0.92}}>{phone}</div>}
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.2)",border:"none",color:"#fff",width:32,height:32,borderRadius:"50%",cursor:"pointer",fontSize:18,fontWeight:600}}>×</button>
        </div>

        {/* Messages area */}
        <div ref={scrollRef} style={{flex:1,overflowY:"auto",padding:"16px 18px",background:C.cream}}>
          {loading ? (
            <div style={{textAlign:"center",padding:30,color:C.muted,fontSize:12}}>Loading conversation...</div>
          ) : messages.length === 0 ? (
            <div style={{textAlign:"center",padding:40,color:C.dim,fontSize:12}}>
              <div style={{fontSize:36,marginBottom:8}}>💬</div>
              <div style={{fontWeight:600,marginBottom:4,color:C.muted}}>No messages yet</div>
              <div style={{fontSize:11}}>Type a message below to start the conversation</div>
            </div>
          ) : (
            messages.map((m, i) => {
              const isOut = m.direction === "outbound";
              const isSystem = m.direction === "system";
              if (isSystem) {
                return <div key={m.id || i} style={{textAlign:"center",fontSize:10,color:C.muted,padding:"8px 0",fontStyle:"italic"}}>{m.body}</div>;
              }
              return (
                <div key={m.id || i} style={{display:"flex",justifyContent: isOut ? "flex-end" : "flex-start",marginBottom:10}}>
                  <div style={{
                    maxWidth:"76%",
                    padding:"8px 14px",
                    background: isOut ? C.gold : "#fff",
                    color: isOut ? "#fff" : C.text,
                    borderRadius: isOut ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    border: isOut ? "none" : "1px solid "+C.border,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    opacity: m._pending ? 0.6 : 1,
                  }}>
                    <div style={{fontSize:13,lineHeight:1.4,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{m.body}</div>
                    <div style={{fontSize:9,marginTop:3,opacity:0.7,textAlign:"right"}}>
                      {m._pending ? "Sending..." : new Date(m.created_at || m.sent_at || m.inserted_at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"})}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Composer */}
        <div style={{padding:"12px 14px",background:C.surface,borderTop:"1px solid "+C.border}}>
          {error && <div style={{padding:"8px 12px",marginBottom:8,background:C.redL,border:"1px solid "+C.red+"40",borderRadius:8,fontSize:11,color:C.red,fontWeight:600}}>⚠️ {error}</div>}
          {success && <div style={{padding:"6px 12px",marginBottom:8,background:C.greenL,border:"1px solid "+C.green+"40",borderRadius:8,fontSize:11,color:C.green,fontWeight:600}}>{success}</div>}

          <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
            <textarea
              value={text}
              onChange={e=>setText(e.target.value)}
              onKeyDown={e=>{ if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); if (!sending) send(); } }}
              placeholder={phone ? "Type a message..." : "⚠️ No phone number - add one to send"}
              rows={2}
              autoFocus
              style={{
                flex:1, padding:"10px 12px",
                background: C.light,
                border:"1px solid "+C.border, borderRadius:10,
                fontSize:13, fontFamily:F, color:C.text,
                resize:"none", outline:"none",
                lineHeight:1.4,
              }}
            />
            <button onClick={send} disabled={!text.trim() || sending} style={{
              padding:"10px 18px",
              background: (!text.trim() || sending) ? C.light : "linear-gradient(135deg,"+C.gold+","+C.goldD+")",
              color: (!text.trim() || sending) ? C.muted : "#fff",
              border:"none", borderRadius:10,
              fontSize:12, fontWeight:700,
              cursor: (!text.trim() || sending) ? "not-allowed" : "pointer",
              fontFamily:F,
              minWidth:80, height:42,
              transition:"all 0.15s",
            }}>
              {sending ? "..." : "Send"}
            </button>
          </div>
          <div style={{fontSize:9,color:C.muted,marginTop:6,textAlign:"center"}}>
            Enter to send · Shift+Enter for new line · {phone ? "via GHL (A2P approved)" : "Add a phone number to send"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SMSModal;
